/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { FeatureFlags } from '@constants/featureFlags';
import { Address, CarrierDetails } from '@store/carriers';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLPricingRequestType } from '@store/ltl';
import * as yup from 'yup';

export const carrierDetailsAdjustmentSchema = yup.object().shape({
  adjustments: yup.array().of(
    yup.object().shape({
      account: yup.number().required('Account is required'),
      amount: yup.string().required('Amount is required'),
      description: yup.string().required('Description is required'),
      loadNumber: yup.string().required('Load number is required'),
      carrierId: yup.number().required('Carrier ID is required'),
    }),
  ),
});

export const insuranceSchema = yup.object({
  type: yup.string().required('Insurance Type is required'),
  insurerName: yup.string().required('Insurer Name is required'),
  policyNumber: yup.string().required('Policy Number is required'),
  effectiveDate: yup.string().required('Effective Date is required'),
  expirationDate: yup
    .string()
    .required('Expiration Date is required')
    .test(
      'is-after-effective-date',
      'Expiration Date must be after Effective Date',
      function (value) {
        const { effectiveDate } = this.parent;
        if (!Boolean(effectiveDate) || !value) return true; // Skip validation if either date is missing
        return new Date(value) > new Date(effectiveDate as string); // Ensure expiration date is after effective date
      },
    ),
  limit: yup.string().required('Limit is required'),
  limitType: yup.string().required('Limit Type is required'),
});

export const carrierDetailsSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  ltlTerminalLink: yup
    .string()
    .nullable()
    .url('Invalid URL format')
    .test(
      'is-valid-url',
      'Invalid URL, please ensure the URL is entered in correct format',
      (value) => {
        if (['', null, undefined].includes(value)) {
          return true;
        }
        const pattern = new RegExp(
          '^(http(s)?:\\/\\/)' +
            '((\\bw{3}\\.\\b))?' +
            '[-a-zA-Z0-9@@:%._+~#=]{1,256}\\.' +
            '[a-zA-Z0-9()]{1,6}' +
            '\\b(?:[-a-zA-Z0-9()@@:%_\\+.~#?&\\/=]*)$',
        );
        return pattern.test(value as string);
      },
    ),
  mc: yup.string().nullable(),
  notes: yup.string().nullable().max(250, 'Notes cannot exceed 250 characters'),
  remitTo: yup.string().email('Invalid email').nullable(),
  customErrors: yup.string().when(() => {
    return yup
      .string()
      .nullable()
      .test({
        name: 'customErrors',
        test: function () {
          const { createError, parent } = this;
          const carrierDetails = parent as CarrierDetails;
          const errors = [];
          const regex = new RegExp(/^\d{3}-\d{2}-\d{4}$|\d{2}-\d{7}$/);

          if (
            (!isFeatureFlagEnabled(
              FeatureFlags.FeatureCarrierProfileNonUsMcDotExemption,
            )() ||
              carrierDetails.nonUs !== true) &&
            !Boolean(carrierDetails.mc) &&
            !Boolean(carrierDetails.dot)
          ) {
            errors.push('You must specify either an MC number or DOT number.');
          }

          if (
            !regex.test(carrierDetails.taxId) &&
            carrierDetails.status !== 'Unvetted'
          ) {
            errors.push(
              'Please enter a valid Social Security Number 000-00-0000 or Employer Identification Number 00-0000000.',
            );
          }

          if (
            (carrierDetails.usaAddress.enabled &&
              (!carrierDetails.usaAddress.physicalAddress1 ||
                !carrierDetails.usaAddress.physicalCity ||
                !carrierDetails.usaAddress.physicalZip ||
                !carrierDetails.usaAddress.physicalStateOrProvince)) ||
            (carrierDetails.canAddress.enabled &&
              (!carrierDetails.canAddress.physicalAddress1 ||
                !carrierDetails.canAddress.physicalCity ||
                !carrierDetails.canAddress.physicalZip ||
                !carrierDetails.canAddress.physicalStateOrProvince)) ||
            (carrierDetails.mexAddress.enabled &&
              (!carrierDetails.mexAddress.physicalAddress1 ||
                !carrierDetails.mexAddress.physicalCity ||
                !carrierDetails.mexAddress.physicalZip ||
                !carrierDetails.mexAddress.physicalStateOrProvince))
          ) {
            errors.push('A physical address is required.');
          }

          if (
            carrierDetails.status !== 'Unvetted' &&
            ((carrierDetails.usaAddress.enabled &&
              (!carrierDetails.usaAddress.mailingAddress1 ||
                !carrierDetails.usaAddress.mailingCity ||
                !carrierDetails.usaAddress.mailingZip ||
                !carrierDetails.usaAddress.mailingStateOrProvince)) ||
              (carrierDetails.canAddress.enabled &&
                (!carrierDetails.canAddress.mailingAddress1 ||
                  !carrierDetails.canAddress.mailingCity ||
                  !carrierDetails.canAddress.mailingZip ||
                  !carrierDetails.canAddress.mailingStateOrProvince)) ||
              (carrierDetails.mexAddress.enabled &&
                (!carrierDetails.mexAddress.mailingAddress1 ||
                  !carrierDetails.mexAddress.mailingCity ||
                  !carrierDetails.mexAddress.mailingZip ||
                  !carrierDetails.mexAddress.mailingStateOrProvince)))
          ) {
            errors.push('A mailing address is required.');
          }

          if (
            carrierDetails.factor &&
            carrierDetails.usaAddress.enabled &&
            !Boolean(carrierDetails.usaAddress.factoringCompanyId)
          ) {
            errors.push(
              'If the carrier factors, you must assign a factoring company to the US address.',
            );
          }

          if (
            carrierDetails.factor &&
            carrierDetails.canAddress.enabled &&
            !Boolean(carrierDetails.canAddress.factoringCompanyId)
          ) {
            errors.push(
              'If the carrier factors, you must assign a factoring company to the Canadian address.',
            );
          }

          if (
            carrierDetails.factor &&
            carrierDetails.mexAddress.enabled &&
            !Boolean(carrierDetails.mexAddress.factoringCompanyId)
          ) {
            errors.push(
              'If the carrier factors, you must assign a factoring company to the Mexican address.',
            );
          }

          if (
            carrierDetails.vendorHold &&
            (!Boolean(carrierDetails.vendorHoldReason) ||
              !Boolean(carrierDetails.vendorHoldDescription))
          ) {
            errors.push('Please enter a Vendor Hold Reason and Description.');
          }

          if (errors.length > 0) {
            return createError({
              message: errors.join(' | '),
            });
          }

          return true;
        },
      });
  }),
  usaAddress: yup.object().shape({
    physicalAddress1: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'Physical Address is required',
            });
          }
          return true;
        },
      }),
    physicalCity: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'City is required',
            });
          }
          return true;
        },
      }),
    physicalZip: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'Zip is required',
            });
          }
          return true;
        },
      }),
    physicalStateOrProvince: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;

          // Check if the address is enabled and the value is not provided
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'State is required',
            });
          }

          // Check if the value contains any numbers
          if (/\d/.test(value ?? '')) {
            return this.createError({
              message: 'State cannot contain numbers',
            });
          }

          return true;
        },
      }),
    mailingAddress1: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'Mailing Address is required',
            });
          }

          return true;
        },
      }),
    mailingCity: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'City is required',
            });
          }

          return true;
        },
      }),
    mailingZip: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'Zip is required',
            });
          }

          return true;
        },
      }),
    mailingStateOrProvince: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'State is required',
            });
          }

          if (value && value.length > 0 && /\d/.test(value)) {
            return this.createError({
              message: 'State cannot contain numbers',
            });
          }

          return true;
        },
      }),
    achRouting: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && Boolean(value) && value?.length !== 9) {
            return this.createError({
              message: 'Routing number must be 9 digits',
            });
          }
          return true;
        },
      }),
  }),
  canAddress: yup.object().shape({
    physicalAddress1: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'Physical Address is required',
            });
          }
          return true;
        },
      }),
    physicalCity: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'City is required',
            });
          }
          return true;
        },
      }),
    physicalZip: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'Zip is required',
            });
          }
          return true;
        },
      }),
    physicalStateOrProvince: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'State is required',
            });
          }
          return true;
        },
      }),
    mailingAddress1: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value)
          ) {
            return this.createError({
              message: 'Mailing Address is required',
            });
          }
          return true;
        },
      }),
    mailingCity: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);
          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value)
          ) {
            return this.createError({
              message: 'City is required',
            });
          }
          return true;
        },
      }),
    mailingZip: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);
          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value)
          ) {
            return this.createError({
              message: 'Zip is required',
            });
          }
          return true;
        },
      }),
    mailingStateOrProvince: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);
          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value)
          ) {
            return this.createError({
              message: 'State is required',
            });
          }
          return true;
        },
      }),
    achRouting: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && Boolean(value) && value?.length !== 9) {
            return this.createError({
              message: 'Routing number must be 9 digits',
            });
          }
          return true;
        },
      }),
  }),
  mexAddress: yup.object().shape({
    physicalAddress1: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'Physical Address is required',
            });
          }
          return true;
        },
      }),
    physicalCity: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'City is required',
            });
          }
          return true;
        },
      }),
    physicalZip: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'Zip is required',
            });
          }
          return true;
        },
      }),
    physicalStateOrProvince: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;

          // Check if the address is enabled and the value is not provided
          if (address.enabled && !Boolean(value)) {
            return this.createError({
              message: 'State is required',
            });
          }

          // Check if the value contains any numbers
          if (/\d/.test(value ?? '')) {
            return this.createError({
              message: 'State cannot contain numbers',
            });
          }

          return true;
        },
      }),
    mailingAddress1: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'Mailing Address is required',
            });
          }

          return true;
        },
      }),
    mailingCity: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'City is required',
            });
          }

          return true;
        },
      }),
    mailingZip: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'Zip is required',
            });
          }

          return true;
        },
      }),
    mailingStateOrProvince: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent, from } = this;
          const address = parent as Address;
          const mainParent = from && (from[1].value as CarrierDetails);

          if (
            address.enabled &&
            mainParent &&
            !Boolean(mainParent.factor) &&
            !Boolean(value) &&
            mainParent.status !== 'Unvetted'
          ) {
            return this.createError({
              message: 'State is required',
            });
          }

          if (value && value.length > 0 && /\d/.test(value)) {
            return this.createError({
              message: 'State cannot contain numbers',
            });
          }

          return true;
        },
      }),
    achRouting: yup
      .string()
      .nullable()
      .test({
        test: function (value) {
          const { parent } = this;
          const address = parent as Address;
          if (address.enabled && Boolean(value) && value?.length !== 9) {
            return this.createError({
              message: 'Routing number must be 9 digits',
            });
          }
          return true;
        },
      }),
  }),
});

export const carrierMaxLiabilitySchema = yup.object({
  pricingRequestType: yup
    .number()
    .required('Pricing Request Type is required')
    .oneOf(
      Object.values(LTLPricingRequestType).filter(
        (value) => typeof value === 'number',
      ),
      'Invalid Pricing Request Type',
    ),
  class: yup.string().required('Class is required'),
  maxNew: yup
    .number()
    .nullable()
    .min(0, 'Max New must be greater than or equal to 0'),
  maxUsed: yup
    .number()
    .nullable()
    .min(0, 'Max Used must be greater than or equal to 0'),
});
